import styled, { keyframes } from "styled-components";

import {
  ANIMATION,
  QUERIES,
  SPACING,
  SPACING_RESPONSIVE,
} from "../../../../../utils/constants";
import { toneColor } from "../../../../../utils";
import { HoldingTone } from "./types";
import { BaseButton } from "../../../../../components/Button";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Section = styled.section`
  margin: ${SPACING_RESPONSIVE.mobile.pageMargin}px;
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING_RESPONSIVE.tablet.pageMargin}px;
    margin-top: 0px;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    margin: ${SPACING_RESPONSIVE.desktop.pageMargin}px;
    margin-top: 0px;
  }
`;

/* ----------------------------- Desktop table ----------------------------- */

export const TableCard = styled.div`
  overflow: hidden;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
`;

export const HeaderRow = styled.tr`
  background: var(--color-tab-background);
`;

export const HeaderCell = styled.th<{ $align?: "left" | "right" }>`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  text-align: ${({ $align }) => $align ?? "left"};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-light);
  white-space: nowrap;
`;

export const BodyRow = styled.tr`
  cursor: pointer;
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border-light);
  }

  &:hover {
    background: var(--color-tab-background);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 3px;
    background: var(--color-tab-background);
  }
`;

export const Cell = styled.td<{ $align?: "left" | "right" }>`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  text-align: ${({ $align }) => $align ?? "left"};
  vertical-align: middle;
  font-size: var(--text-base);
  color: var(--color-text-default);
  white-space: nowrap;
`;

export const NameText = styled.div`
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

// Keyboard-accessible target for a desktop row. The row's onClick stays as a
// mouse affordance; this button is what receives focus and Enter/Space.
export const NameButton = styled(BaseButton)`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  font-size: var(--text-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);

  &:hover {
    text-decoration: underline;
  }
`;

export const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  margin-top: ${SPACING.xs}px;
`;

export const Dot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

export const TypeLabel = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const SinceText = styled.span`
  color: var(--color-text-secondary);
`;

export const ValueText = styled.span`
  font-weight: var(--font-weight-bold);
`;

export const MutedText = styled.span`
  color: var(--color-text-secondary);
`;

export const EarnedText = styled.span<{ $tone: HoldingTone }>`
  font-weight: var(--font-weight-medium);
  color: ${({ $tone }) => toneColor($tone)};
`;

export const CashbackNote = styled.span`
  display: block;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
`;

export const TrendCell = styled.td`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  vertical-align: middle;
`;

export const TrendBox = styled.div`
  width: 120px;
  height: 36px;
  margin-left: auto;
`;

export const SummaryFooter = styled.div`
  padding: ${SPACING.md}px ${SPACING_RESPONSIVE.desktop.cardPadding}px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-tab-background);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

/* ------------------------------ Mobile cards ----------------------------- */

export const CardList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Card = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING_RESPONSIVE.mobile.cardPadding}px;
  border-radius: ${SPACING.md}px;
  background: var(--color-card-background);
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-small);
  cursor: pointer;
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};

  &:hover {
    background: var(--color-tab-background);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardValueRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardValue = styled.span`
  font-size: var(--text-xxl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-default);
`;

export const CardGain = styled.span<{ $tone: HoldingTone }>`
  font-size: var(--text-base);
  font-weight: var(--font-weight-medium);
  color: ${({ $tone }) => toneColor($tone)};
`;

export const CardTrendBox = styled.div`
  width: 100%;
  height: 64px;
`;

export const SinceFooter = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const MobileSummary = styled.p`
  margin: ${SPACING.md}px 0 0;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  text-align: center;
`;
